class LineItems extends HTMLElement {
  section =
    window.location.pathname === `${Shopify.routes.root}cart`
      ? 'fetch-line-items-cart-page'
      : 'fetch-line-items';

  connectedCallback() {
    this.unsubscribeCartChange = subscribe('cart:change', ({ line, quantity }) =>
      this.cartChange(line, quantity)
    );
    this.unsubscribeRefreshCart = subscribe('cart:added', (payload) => this.onCartAdded(payload));
    this.subscribeEasyGift();
  }

  disconnectedCallback() {
    this.unsubscribeCartChange();
    this.unsubscribeRefreshCart();
  }

  /**
   * Change the cart.
   * @description Use the /{locale}/cart/change.js endpoint to change the quantity, properties, and selling_plan properties of a cart line item. Only items already in your cart can be changed, and you can change only one line item at a time.
   * @tutorial https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-change-js
   */
  async cartChange(line, quantity) {
    this.lastCartAction = Date.now();

    publish('cart:updating', {
      line,
    });

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.section,
      sections_url: window.location.pathname,
    });

    try {
      const { error, data } = await this.fetchCart(routes.cart_change_url, body);

      if (error) throw new Error(error);
      this.renderSections(data.sections);

      publish('cart:updated', {
        itemCount: data.item_count,
      });

      window.toast.success('Cart updated');
    } catch (error) {
      console.error(error);
      window.toast.error(error.message);

      publish('cart:error', {
        line,
      });
    }
  }

  /**
   * Fetch the cart.
   * @param {string} input - The URL to fetch.
   * @param {string} body - The request body.
   * @returns {Promise} The response.
   */
  async fetchCart(input, body) {
    const response = await fetch(input, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body,
    });

    const data = await response.json();

    if (!response.ok) return { error: data.errors, data: null };

    return { error: null, data };
  }

  /**
   * Cart added.
   * @description Cart drawer lag (28 Oct). product-form.js asks for the drawer sections in the same
   * /cart/add.js request, so the drawer opens with the new lines straight away instead of the
   * previous contents or the empty state. Add flows that publish without sections (Kaching
   * add-ons, bundle sections, landing pages, cross-sell) fall back to a refresh.
   * @param {{ sections?: Object }} [payload] - The cart:added payload.
   */
  onCartAdded(payload) {
    this.lastCartAction = Date.now();

    const sections = payload?.sections;
    if (sections?.[this.section]) {
      this.renderSections(sections);
      publish('cart:updated', {
        itemCount: this.readItemCount(sections),
      });
      return;
    }

    this.refreshCart();
  }

  /**
   * Refresh the cart.
   * @description Cart drawer lag (28 Oct). Reads the sections with a GET request (Section Rendering
   * API) instead of an empty POST to /cart/update. The EG Auto Add to Cart app holds every
   * /cart/update request without updates for 2 seconds (its delayUpdates setting), which kept the
   * drawer on its previous contents, or on the empty state, after every add to cart. The item
   * count comes from the fetch-cart-item-count section in the same request. A refresh that is
   * asked for while one runs is done once more afterwards, so the last cart state always wins.
   * @param {{ quiet?: boolean }} [options] - quiet keeps the lines as they are while loading.
   */
  async refreshCart({ quiet = false } = {}) {
    if (this.refreshing) {
      this.refreshQueued = true;
      return;
    }

    this.refreshing = true;
    if (!quiet) this.setBusy(true);

    try {
      const response = await fetch(
        `${Shopify.routes.root}?sections=${this.section},fetch-cart-item-count`,
        { headers: { Accept: 'application/json' } }
      );
      if (!response.ok) throw new Error(window.toast_translations?.cart?.error || 'Cart could not be loaded');

      const sections = await response.json();
      this.renderSections(sections);

      publish('cart:updated', {
        itemCount: this.readItemCount(sections),
      });
    } catch (error) {
      window.toast.error(error.message);
    } finally {
      this.refreshing = false;
      this.setBusy(false);

      if (this.refreshQueued) {
        this.refreshQueued = false;
        this.refreshCart({ quiet: true });
      }
    }
  }

  /**
   * Read the item count.
   * @param {Object} sections - Rendered sections keyed by section id.
   * @returns {number|undefined} The cart item count, when the count section is included.
   */
  readItemCount(sections) {
    const html = sections?.['fetch-cart-item-count'];
    if (!html) return undefined;

    const element = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-item-count');
    const count = parseInt(element?.textContent ?? '', 10);

    return Number.isNaN(count) ? undefined : count;
  }

  /**
   * Busy state.
   * @description While the drawer waits for new lines it keeps the current lines faded with the
   * theme's opacity class, and hides the empty state, so a stale "Your cart is currently empty"
   * never shows after an add to cart.
   * @param {boolean} busy - Whether a refresh is running.
   */
  setBusy(busy) {
    this.toggleAttribute('aria-busy', busy);

    const lines = this.querySelector('[data-cart-lines]');
    lines?.classList.toggle('opacity-50', busy);
    lines?.classList.toggle('pointer-events-none', busy);
    this.querySelector('.cart-empty-state')?.classList.toggle('invisible', busy);
  }

  /**
   * EG Auto Add to Cart.
   * @description The app adds and removes gifts with its own cart requests, which the theme does
   * not see. Its afterCartRequest event refreshes the lines, but only shortly after a cart action
   * in this visit, so the app's own requests on page load do not trigger a refresh.
   */
  subscribeEasyGift() {
    const subscribeEvents = () => {
      const events = window.EasyGift?.Events;
      if (typeof events?.subscribe !== 'function') return false;
      if (this.easyGiftSubscribed) return true;

      this.easyGiftSubscribed = true;
      events.subscribe('afterCartRequest', () => {
        if (!this.lastCartAction || Date.now() - this.lastCartAction > 10000) return;
        this.refreshCart({ quiet: true });
      });
      return true;
    };

    if (!subscribeEvents()) window.addEventListener('load', subscribeEvents, { once: true });
  }

  /**
   * Render the sections.
   * @description Replaces only the cart-dependent rows when [data-cart-lines] is present,
   * so cart-recommendations is not destroyed, does not re-fetch, and does not collapse the
   * drawer height on every change. Falls back to the full swap on older markup.
   */
  renderSections(sections) {
    const html = new DOMParser().parseFromString(sections[this.section], 'text/html');

    const next = html.querySelector('[data-cart-lines]');
    const current = this.querySelector('[data-cart-lines]');

    if (next && current) {
      current.replaceWith(next);
      return;
    }

    this.innerHTML = html.querySelector('line-items').innerHTML;
  }
}

customElements.define('line-items', LineItems);
